// ─── BUNDEL di keranjang & checkout — helper MURNI (tanpa I/O, tanpa React) ──
//
// Rancangan: docs/RENCANA-bundling-produk.md §4 & §6.3–§6.4. Rumus bundel
// sendiri hidup di `src/lib/bundle.ts`; berkas ini hanya MERANGKAI angka
// bundel dengan keranjang biasa, pratinjau voucher, pixel, dan cadangan
// "tak satu bundel pun lebih hemat".
//
// K5 (putusan pemilik 2026-10-21): voucher BOLEH di atas bundel. Mesin voucher
// berjalan apa adanya atas harga TERPISAH komponen (baris bundel dikirim ke
// `preview_my_voucher` dengan `unit_price = baseUnitPrice`, persis seperti RPC
// `checkout_orders_with_bundles` mengirimnya ke `voucher_evaluate_cart`), lalu
// langkah bundel diterapkan ulang sebagai RASIO terkunci
// (`applyBundleAfterVoucher`). Ringkasan keranjang mengikuti rantai nota:
//   Harga umum − Potongan voucher − Potongan promo − Diskon level − Potongan
//   bundel = Total
// dengan "Potongan voucher" = nilai voucher yang dicatat server (dihitung atas
// harga terpisah) dan "Potongan bundel" = harga terpisah sesudah voucher −
// harga bundel akhir (kolom `bundle_base_unit_price − unit_price`).
//
// Semua angka di sini TAMPILAN: checkout menghitung ulang dari DB dan RPC
// memverifikasinya — kecuali gerbang "harga berubah → konfirmasi" (§6.4,
// `bundlePriceIncreases`), yang membandingkan hitungan ulang itu dengan angka
// yang DILIHAT pembeli sebelum satu baris pun ditulis. Nama tampil selalu "bundel" — "paket" sudah berarti paket
// pengiriman.

import {
  applyBundleAfterVoucher,
  bundleNotApplicableText,
  bundleSavingPercent,
  type BundleQuote
} from "@/lib/bundle";
import type { CartBundle } from "@/lib/bundleCart";
import type { CartLine, CartState } from "@/lib/cartMulti";
import type { PixelCart } from "@/lib/pixel";
import { voucherCheckoutLinePayload, type VoucherRpcLine } from "@/lib/voucher";
import {
  buildCartVoucherSummary,
  type CartVoucherPreviewLine,
  type CartVoucherPricedLine,
  type CartVoucherSelectionLike,
  type CartVoucherSummary
} from "@/lib/voucherCart";

// ─── Tipe ────────────────────────────────────────────────────────────────────

/**
 * Satu baris komponen bundel yang sudah berharga — subset struktural
 * `ResolvedBundleLine` (`src/lib/supabase/data.ts`), supaya helper ini bisa
 * diuji tanpa lapisan data.
 */
export type BundleCheckoutLine = CartVoucherPricedLine & {
  componentId: string;
  productId: string;
  /** Posisi komponen di bundel — bahan kunci baris `b<i>:<posisi>`. */
  position?: number;
  /** Harga TERPISAH per pcs pra-voucher (termasuk tambahan); null = harga menyusul. */
  baseUnitPrice: number | null;
};

/** Satu bundel keranjang yang sudah dihitung (subset struktural `ResolvedBundle`). */
export type BundleCheckoutEntry = {
  cartKey: string;
  qty: number;
  quote: BundleQuote;
  lines: readonly BundleCheckoutLine[];
};

/** Harga bundel terkini di keranjang: berhasil dihitung, atau tak tersedia + sebabnya. */
export type BundleLiveState<E extends BundleCheckoutEntry = BundleCheckoutEntry> =
  | { status: "ok"; entry: E }
  | { status: "error"; message: string };

/** Ringkasan voucher keranjang ber-bundel — `CartVoucherSummary` + potongan bundel. */
export type BundleCartVoucherSummary = CartVoucherSummary & { bundleDiscount: number };

const isPrice = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

// ─── Kunci & tanda tangan ────────────────────────────────────────────────────

/** Kunci baris komponen — cermin `resolveBundlesPricing`: `b<urutan bundel, 1-based>:<posisi>`. */
export function bundleLineKey(index: number, position: number): string {
  return `b${index + 1}:${position}`;
}

/**
 * Entri hasil resolusi SATU bundel (dipanggil sendiri-sendiri supaya galat
 * satu bundel tak menyembunyikan yang lain) memakai kunci `b1:*`. Kunci itu
 * ditulis ulang ke urutan bundel di keranjang supaya baris dua bundel tak
 * pernah bertabrakan di pratinjau voucher. Fungsi murni.
 */
export function rekeyBundleEntry<
  E extends { lines: readonly (BundleCheckoutLine & { key: string })[] }
>(entry: E, index: number): E {
  return {
    ...entry,
    lines: entry.lines.map((l, li) => ({ ...l, key: bundleLineKey(index, l.position ?? li + 1) }))
  } as E;
}

/** Tanda tangan isi bundel keranjang (kunci + jumlah) — kunci efek berupa STRING. */
export function bundleCartSignature(bundles: readonly Pick<CartBundle, "key" | "qty">[]): string {
  return bundles.length ? JSON.stringify(bundles.map((b) => [b.key, b.qty])) : "";
}

/** Id produk komponen bundel (tanpa duplikat, urut kemunculan) — bahan kandidat voucher. */
export function bundleCartProductIds(bundles: readonly Pick<CartBundle, "choices">[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of bundles) {
    for (const c of b.choices) {
      if (c.productId && !seen.has(c.productId)) {
        seen.add(c.productId);
        out.push(c.productId);
      }
    }
  }
  return out;
}

/** Galat resolusi harga bundel → kalimat pembeli (galat jaringan tak dipajang mentah). */
export function bundleLiveErrorText(raw: unknown): string {
  const msg = raw instanceof Error ? raw.message : typeof raw === "string" ? raw : "";
  if (!msg.trim() || /failed to fetch|network|timeout|load failed|fetch failed/i.test(msg)) {
    return "Harga bundling gagal dimuat — periksa koneksi, lalu coba lagi.";
  }
  return msg;
}

// ─── Harga akhir (K5) ────────────────────────────────────────────────────────

/** Harga sesudah voucher per kunci baris — HANYA baris layak dengan angka sah. */
function previewFinalByKey(previewLines: readonly CartVoucherPreviewLine[] | null): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of previewLines ?? []) {
    if (l.eligible && isPrice(l.unitPrice)) out.set(l.key, l.unitPrice);
  }
  return out;
}

/**
 * Baris pratinjau voucher (K5): komponen bundel dikirim dengan harga TERPISAH
 * (`unitPrice = baseUnitPrice`) — sama dengan muatan `voucher_evaluate_cart`
 * di RPC `checkout_orders_with_bundles`, jadi angka pratinjau = angka checkout.
 */
export function bundleVoucherLines<L extends BundleCheckoutLine>(
  entries: readonly { lines: readonly L[] }[]
): Array<L & { unitPrice: number | null }> {
  return entries.flatMap((e) => e.lines.map((l) => ({ ...l, unitPrice: l.baseUnitPrice })));
}

/**
 * Harga satuan AKHIR tiap baris komponen bundel, per kunci baris — cermin
 * blok K5 `checkout_orders_with_bundles`:
 *   • bundel berlaku, tanpa voucher → harga alokasi bundel (`quote.lines`);
 *   • bundel berlaku + voucher → harga terpisah SESUDAH voucher per komponen
 *     (baris layak = jawaban pratinjau; tak layak = harga terpisahnya), lalu
 *     rasio bundel terkunci (`applyBundleAfterVoucher`);
 *   • bundel TAK berlaku → dipesan sebagai baris biasa: harga terpisah, atau
 *     harga sesudah voucher bila barisnya layak.
 * null = harga tak diketahui (menyusul).
 */
export function bundleFinalUnitPrices(
  entries: readonly BundleCheckoutEntry[],
  previewLines: readonly CartVoucherPreviewLine[] | null
): Map<string, number | null> {
  const after = previewFinalByKey(previewLines);
  const out = new Map<string, number | null>();
  for (const e of entries) {
    if (!e.quote.applies) {
      for (const l of e.lines) out.set(l.key, after.get(l.key) ?? (isPrice(l.baseUnitPrice) ? l.baseUnitPrice : null));
      continue;
    }
    const quote = e.quote;
    const quoteByComponent = new Map(quote.lines.map((q) => [q.componentId, q] as const));
    if (!previewLines) {
      for (const l of e.lines) {
        const q = quoteByComponent.get(l.componentId);
        out.set(l.key, q ? q.unitPrice : isPrice(l.unitPrice) ? l.unitPrice : null);
      }
      continue;
    }
    const finals = applyBundleAfterVoucher(
      quote,
      e.lines.map((l) => {
        const q = quoteByComponent.get(l.componentId);
        const separate = q?.baseUnitPrice ?? (isPrice(l.baseUnitPrice) ? l.baseUnitPrice : 0);
        return {
          componentId: l.componentId,
          priceExtra: q?.priceExtra ?? Math.max(0, Number(l.priceExtra ?? 0) || 0),
          unitAfterVoucher: after.get(l.key) ?? separate,
          // mig 8090 (D15-32): komponen tak layak tetap pada harga bundelnya.
          eligible: after.has(l.key),
          unitWithoutVoucher: q?.unitPrice
        };
      })
    );
    for (const l of e.lines) out.set(l.key, finals.get(l.componentId) ?? null);
  }
  return out;
}

/**
 * Ringkasan Harga umum · Potongan voucher · Potongan promo · Diskon level ·
 * Potongan bundel · Total untuk keranjang yang memuat bundel.
 *
 * Langkah 1 = `buildCartVoucherSummary` APA ADANYA atas baris biasa + baris
 * komponen ber-harga TERPISAH (dunia mesin voucher). Langkah 2 = potongan
 * bundel = Σ (harga terpisah sesudah voucher − harga bundel akhir) × qty, dan
 * Total turun sebesar itu. Kelima potongan tetap SALING MENJUMLAH:
 * umum − voucher − promo − level − bundel = total.
 */
export function buildBundleCartVoucherSummary(
  carts: ReadonlyArray<{ lines: readonly CartVoucherPricedLine[] }>,
  entries: readonly BundleCheckoutEntry[],
  selection: CartVoucherSelectionLike | null
): BundleCartVoucherSummary {
  const separateRows = [...carts, ...entries.map((e) => ({ lines: bundleVoucherLines([e]) }))];
  const base = buildCartVoucherSummary(separateRows, selection);
  const previewLines = selection?.preview.lines ?? null;
  const after = previewFinalByKey(previewLines);
  const finals = bundleFinalUnitPrices(entries, previewLines);
  let bundleDiscount = 0;
  for (const e of entries) {
    for (const l of e.lines) {
      const qty = Math.max(0, Math.floor(Number(l.qty) || 0));
      // Aturan lewati yang SAMA dengan langkah 1 (baris tanpa harga umum /
      // tanpa harga = "harga menyusul", tak masuk total).
      if (qty <= 0 || l.normalPrice === null || !isPrice(l.baseUnitPrice)) continue;
      const separateAfter = after.get(l.key) ?? l.baseUnitPrice;
      const fin = finals.get(l.key);
      if (!isPrice(fin)) continue;
      bundleDiscount += Math.max(0, separateAfter - fin) * qty;
    }
  }
  return { ...base, bundleDiscount, total: base.total - bundleDiscount };
}

/**
 * Hemat NYATA voucher untuk keranjang ber-bundel (tiket /keranjang/voucher).
 * `quote` = `quoteProductVoucher` atas baris biasa + komponen ber-harga
 * terpisah; bagian komponen bundel diganti selisih harga bundel pra-voucher →
 * harga bundel sesudah voucher (rasio terkunci K5).
 */
export function bundleAwareVoucherSavings(
  quote: { savings: number; lines: readonly { key: string; unitPrice: number }[] },
  entries: readonly BundleCheckoutEntry[]
): number {
  const bundleLines = new Map<string, BundleCheckoutLine>();
  for (const e of entries) for (const l of e.lines) bundleLines.set(l.key, l);
  if (!bundleLines.size) return quote.savings;
  // Bagian baris biasa = savings mesin − porsi baris komponen (harga terpisah).
  let ordinary = quote.savings;
  for (const q of quote.lines) {
    const l = bundleLines.get(q.key);
    if (!l || !isPrice(l.baseUnitPrice)) continue;
    ordinary -= Math.max(0, l.baseUnitPrice - q.unitPrice) * Math.max(0, Math.floor(l.qty));
  }
  const finals = bundleFinalUnitPrices(
    entries,
    quote.lines.map((q) => ({ key: q.key, eligible: true, unitPrice: q.unitPrice }))
  );
  const pre = bundleFinalUnitPrices(entries, null);
  let bundle = 0;
  for (const [key, l] of bundleLines) {
    const a = pre.get(key);
    const b = finals.get(key);
    if (!isPrice(a) || !isPrice(b)) continue;
    bundle += Math.max(0, a - b) * Math.max(0, Math.floor(l.qty));
  }
  return Math.max(0, ordinary) + bundle;
}

// ─── Tampilan kartu bundel ───────────────────────────────────────────────────

export type BundleCardPrice =
  /** Harga potret keranjang (tamu, atau harga terkini belum termuat). */
  | {
      kind: "snapshot";
      perBundle: number | null;
      separate: number | null;
      saving: number;
      savingPercent: number;
      /** Σ tambahan per bundel di potret (0 = tak ada / tak diketahui, entri lama). */
      extras: number;
    }
  /** Bundel berlaku untuk pembeli ini. */
  | {
      kind: "bundle";
      perBundle: number;
      separate: number;
      saving: number;
      savingPercent: number;
      /** Σ harga tambahan varian pilihan yang termasuk di harga per bundel. */
      extras: number;
    }
  /** Bundel tak lebih hemat → dipesan dengan harga satuan. */
  | { kind: "separate"; perBundle: number | null; note: string; extras: number }
  /** Bundel tak bisa dipesan (berakhir, tak berhak, isi berubah, gagal dimuat). */
  | { kind: "error"; message: string };

export function bundleCardPrice(
  bundle: Pick<CartBundle, "displayTotal" | "displaySeparate" | "displayExtras">,
  live: BundleLiveState | undefined
): BundleCardPrice {
  if (!live) {
    const perBundle = isPrice(bundle.displayTotal) ? bundle.displayTotal : null;
    const separate =
      perBundle !== null && isPrice(bundle.displaySeparate) && bundle.displaySeparate > perBundle
        ? bundle.displaySeparate
        : null;
    const saving = separate !== null && perBundle !== null ? separate - perBundle : 0;
    // Tambahan potret (D15-11/26): termasuk di kedua harga, jadi hemat (selisih)
    // sudah atas dasar; pembagi % = dasar terpisah. Entri lama tanpa potret
    // tambahan = 0 (perilaku lama).
    const extras =
      typeof bundle.displayExtras === "number" && Number.isFinite(bundle.displayExtras) && bundle.displayExtras > 0
        ? bundle.displayExtras
        : 0;
    return {
      kind: "snapshot",
      perBundle,
      separate,
      saving,
      savingPercent: separate !== null ? bundleSavingPercent(saving, separate - extras) : 0,
      extras
    };
  }
  if (live.status === "error") return { kind: "error", message: live.message };
  const quote = live.entry.quote;
  if (quote.applies) {
    return {
      kind: "bundle",
      perBundle: quote.bundleTotal,
      separate: quote.separateTotal,
      saving: quote.saving,
      savingPercent: bundleSavingPercent(quote.saving, quote.separateBase),
      extras: quote.extrasTotal
    };
  }
  return {
    kind: "separate",
    perBundle: quote.separateTotal,
    note: bundleNotApplicableText(quote.reason),
    extras: quote.extrasTotal ?? 0
  };
}

/** Harga per 1 bundel yang dipajang kartu; null = tak diketahui / tak tersedia. */
export function bundleCardUnitTotal(price: BundleCardPrice): number | null {
  return price.kind === "error" ? null : price.perBundle;
}

/**
 * Σ harga bundel keranjang (harga per bundel × jumlah). null bila satu bundel
 * pun harganya tak diketahui atau tak tersedia — idiom `cartSubtotal`.
 */
export function cartBundlesTotal(
  bundles: readonly Pick<CartBundle, "key" | "qty" | "displayTotal" | "displaySeparate">[],
  liveByKey: ReadonlyMap<string, BundleLiveState> | null
): number | null {
  let total = 0;
  for (const b of bundles) {
    const unit = bundleCardUnitTotal(bundleCardPrice(b, liveByKey?.get(b.key)));
    if (unit === null) return null;
    total += unit * b.qty;
  }
  return total;
}

/**
 * Total semua barang keranjang = subtotal baris biasa + Σ bundel. Keranjang
 * tanpa baris biasa → total bundel saja (`cartSubtotal([])` = null bukan
 * "harga menyusul"). null = ada harga yang belum diketahui.
 */
export function cartGrandTotal(
  cartsSubtotal: number | null,
  cartsCount: number,
  bundlesTotal: number | null
): number | null {
  if (bundlesTotal === null) return null;
  if (cartsCount === 0) return bundlesTotal;
  return cartsSubtotal === null ? null : cartsSubtotal + bundlesTotal;
}

/**
 * Baris komponen bundel per PRODUK (qty = jumlah bundel, harga = harga bundel
 * pra-voucher bila diketahui) — bahan DP keranjang: pesanan PO satu produk
 * memuat baris biasa DAN baris bundelnya (RPC menggabungkannya).
 */
export function bundleLinesByProduct(
  bundles: readonly Pick<CartBundle, "key" | "qty" | "choices">[],
  liveByKey: ReadonlyMap<string, BundleLiveState> | null
): Map<string, Array<{ qty: number; unitPrice: number | null }>> {
  const out = new Map<string, Array<{ qty: number; unitPrice: number | null }>>();
  for (const b of bundles) {
    const live = liveByKey?.get(b.key);
    const finals = live?.status === "ok" ? bundleFinalUnitPrices([live.entry], null) : null;
    const lineByComponent =
      live?.status === "ok" ? new Map(live.entry.lines.map((l) => [l.componentId, l] as const)) : null;
    for (const c of b.choices) {
      const line = lineByComponent?.get(c.componentId);
      const unit = line && finals ? finals.get(line.key) ?? null : null;
      const list = out.get(c.productId) ?? [];
      list.push({ qty: b.qty, unitPrice: unit });
      out.set(c.productId, list);
    }
  }
  return out;
}

// ─── Harga berubah → konfirmasi (§6.4) ───────────────────────────────────────

/**
 * Harga per 1 bundel yang AKAN dipesan (pra-voucher) menurut hitungan resolver:
 * harga bundel TERMASUK tambahan bila bundelnya berlaku, harga terpisah bila
 * tidak (dipesan harga satuan). Rumus yang sama dengan kartu keranjang
 * (`bundleCardPrice` → `bundleCardUnitTotal`). null = tak diketahui.
 */
export function bundleQuoteUnitTotal(quote: BundleQuote): number | null {
  if (quote.applies) return quote.bundleTotal;
  return isPrice(quote.separateTotal) ? quote.separateTotal : null;
}

/**
 * Harga per 1 bundel yang DIPAJANG kartu keranjang, per kunci bundel — bahan
 * `expected` checkout. Persis angka kartu: harga terkini (jawaban terakhir)
 * bila ada, potret keranjang (`displayTotal`) bila belum. Bundel yang kartunya
 * tak memajang angka (tak diketahui / tak tersedia) TIDAK dimasukkan — checkout
 * menganggap harganya belum pernah dilihat. Fungsi murni.
 */
export function bundleExpectedTotals(
  bundles: readonly Pick<CartBundle, "key" | "displayTotal" | "displaySeparate">[],
  liveByKey: ReadonlyMap<string, BundleLiveState> | null
): Map<string, number> {
  const out = new Map<string, number>();
  for (const b of bundles) {
    const unit = bundleCardUnitTotal(bundleCardPrice(b, liveByKey?.get(b.key)));
    if (isPrice(unit)) out.set(b.key, unit);
  }
  return out;
}

/**
 * Kunci bundel yang TAK BOLEH dipesan tanpa konfirmasi ulang (§6.4): harga per
 * bundel terkini NAIK dari yang dilihat pembeli, atau tak bisa dibandingkan
 * (tak ada di `expected` / harga terkini tak diketahui). Harga TURUN boleh
 * lanjut — pembeli membayar lebih sedikit dari yang ia setujui. Kosong = boleh
 * lanjut. Pembanding = `bundleQuoteUnitTotal` (pra-voucher, sama dengan kartu).
 *
 * Pengecualian: bundel tak berlaku yang harganya TAK DIKETAHUI saat dipajang
 * (kartu tanpa angka → absen dari `expected`) dan MASIH tak diketahui (mis.
 * `harga_tidak_sah` tanpa harga terpisah) BUKAN kenaikan — tak ada angka yang
 * dilihat lalu dilampaui. Menandainya membuat pembeli terjebak lingkaran
 * BUNDLE_PRICE_CHANGED tanpa akhir (memuat ulang tak pernah memberi angka);
 * bundel itu ditangani cadangan BUNDLE_NONE_APPLIES seperti biasa.
 */
export function bundlePriceIncreases(
  entries: readonly Pick<BundleCheckoutEntry, "cartKey" | "quote">[],
  expected: ReadonlyMap<string, number>
): string[] {
  const out: string[] = [];
  for (const e of entries) {
    const seen = expected.get(e.cartKey);
    const fresh = bundleQuoteUnitTotal(e.quote);
    if (!e.quote.applies && fresh === null && !isPrice(seen)) continue;
    if (!isPrice(seen) || fresh === null || fresh > seen) out.push(e.cartKey);
  }
  return out;
}

// ─── Kunci idempoten checkout ber-bundel ─────────────────────────────────────

/**
 * Kalimat RPC `checkout_orders_with_bundles` untuk `p_bundles` kosong — galat
 * yang dilempar SESUDAH pemeriksaan replay, jadi memulangkannya membuktikan
 * kunci `p_request_id` itu BELUM pernah tercatat di server.
 */
// Kalimat PROTOKOL, tak pernah tampil (dikonsumsi probe replay di bawah) —
// SENGAJA tetap "bundel" saat istilah layar menjadi "Bundling" (2026-10-22):
// mengganti satu sisi saja = probe selalu "tak_tahu".
export const BUNDLE_CHECKOUT_EMPTY_ERROR = "Keranjang tidak memuat bundel";

/**
 * Bolehkah kunci idempoten checkout bundel DILEPAS sesudah percobaan yang
 * gagal dengan galat `raw`? Kunci dilepas HANYA bila terbukti belum tercatat di
 * server; selain itu ia DISIMPAN untuk percobaan berikutnya, yang lalu
 * memeriksa replay lebih dulu (`createCheckoutOrdersWithBundles`
 * `keyOutstanding`) — kunci baru atas percobaan yang hasilnya tak diketahui
 * (respons hilang di jaringan padahal RPC sudah commit) = pesanan GANDA.
 *
 * Terbukti belum tercatat:
 *   • `BUNDLE_INVALID:*`, `VOUCHER_INVALID:*`, `GIFT_INVALID:*` (hadiah
 *     pembelian, mig 8680), `BUNDLE_CHECKOUT_EMPTY_ERROR` — dilempar RPC
 *     SESUDAH replay diperiksa (dan transaksinya batal utuh);
 *   • `BUNDLE_PRICE_CHANGED` / `BUNDLE_NONE_APPLIES` — gerbang klien yang
 *     berhenti SEBELUM RPC: kunci segar belum pernah dikirim, dan kunci yang
 *     tertinggal dari percobaan gagal sudah diperiksa replay-nya lebih dulu
 *     (jawaban `BUNDLE_CHECKOUT_EMPTY_ERROR`) sebelum gerbang ini sempat jalan.
 * Selain itu (jaringan, galat tak dikenal, galat sebelum replay) = tak tahu.
 */
export function bundleCheckoutKeyReleasable(raw: string): boolean {
  return (
    raw === "BUNDLE_PRICE_CHANGED" ||
    raw === "BUNDLE_NONE_APPLIES" ||
    raw === BUNDLE_CHECKOUT_EMPTY_ERROR ||
    /BUNDLE_INVALID:/.test(raw) ||
    /VOUCHER_INVALID:/.test(raw) ||
    /GIFT_INVALID:/.test(raw)
  );
}

/**
 * Putusan probe replay — RPC dikirim dengan kunci LAMA dan `p_bundles` kosong
 * sebelum percobaan ulang berjalan:
 *   • `replay` — percobaan lalu SUDAH tercatat; `data` = hasilnya (pesanan
 *     sudah ada, jangan buat lagi);
 *   • `bebas`  — RPC menolak `p_bundles` kosong SESUDAH memeriksa replay →
 *     kunci terbukti belum tercatat, checkout boleh berjalan seperti biasa;
 *   • `tak_tahu` — jaringan / galat lain (termasuk galat sebelum replay):
 *     kunci tetap disimpan, galatnya diteruskan.
 * Probe tak pernah menulis apa pun: tanpa replay ia pasti berhenti di
 * pemeriksaan `p_bundles` kosong.
 */
export function bundleReplayProbeVerdict(
  data: unknown,
  errorMessage: string | null | undefined
): "replay" | "bebas" | "tak_tahu" {
  if (errorMessage) return errorMessage === BUNDLE_CHECKOUT_EMPTY_ERROR ? "bebas" : "tak_tahu";
  return data && typeof data === "object" && !Array.isArray(data) ? "replay" : "tak_tahu";
}

// ─── Cadangan BUNDLE_NONE_APPLIES ────────────────────────────────────────────

/**
 * Bundel → baris keranjang BIASA, untuk checkout jalur lama saat tak satu
 * bundel pun lebih hemat bagi pembeli ini (aturan termurah §4.2: barisnya
 * dipesan tanpa jejak bundel). Tiap pilihan komponen jadi `qty` pcs varian
 * itu; produk yang sudah ada di keranjang menerima baris/qty tambahan (idiom
 * `addProductToCarts`), produk baru menjadi entri baru dengan potret seadanya:
 * tipe dari bundel, DP/kuota/status tak diketahui (`undefined`) — checkout
 * membaca ulang harga, status, dan kuota dari DB. `unitPriceOf` = harga
 * terpisah terkini per komponen (tampilan) — angka saja, atau `{ price,
 * priceExtra }` supaya baris menyimpan PASANGAN harga/tambahan (D14-31,
 * bughunt D15 T6: tanpa tambahan, jalur cadangan checkout memotret tambahan
 * HARI INI di atas harga keranjang); absen = tak diketahui. Murni.
 */
/**
 * Muatan `p_carts` satu baris BIASA checkout bundel (mig 8070) — satu pembuat
 * dengan checkout ber-voucher (`voucherCheckoutLinePayload`, mig 8080): tanpa
 * kunci `price_extra` RPC menulis NULL dan trigger memotret tambahan varian HARI
 * INI di atas harga keranjang (kelas D14-31; varian warisan sumbu memakai
 * tambahan saudara). Murni.
 */
export function bundleCartLinePayload(line: VoucherRpcLine): Record<string, unknown> {
  return voucherCheckoutLinePayload(line);
}

export function bundlesAsCartLines(
  carts: readonly CartState[],
  bundles: readonly CartBundle[],
  unitPriceOf?: (
    bundleKey: string,
    componentId: string
  ) => number | { price: number | null | undefined; priceExtra?: number | null } | undefined
): CartState[] {
  let next: CartState[] = carts.map((c) => ({ ...c, lines: c.lines.map((l) => ({ ...l })) }));
  for (const b of bundles) {
    if (!(b.qty > 0)) continue;
    for (const choice of b.choices) {
      const got = unitPriceOf?.(b.key, choice.componentId);
      const price = typeof got === "object" && got !== null ? got.price : got;
      const unitPrice = isPrice(price) ? price : undefined;
      const rawExtra = typeof got === "object" && got !== null ? got.priceExtra : undefined;
      // Tambahan hanya bila berpasangan dengan harga yang ditulis, dijepit ≤ harga.
      const paired =
        unitPrice !== undefined && typeof rawExtra === "number" && Number.isFinite(rawExtra) && rawExtra >= 0
          ? { priceExtra: Math.min(Math.round(rawExtra), unitPrice) }
          : {};
      const idx = next.findIndex((c) => c.productId === choice.productId);
      if (idx === -1) {
        const entry: CartState = {
          productId: choice.productId,
          productSlug: choice.productSlug,
          productName: choice.productName,
          productImage: choice.productImage,
          estimatedReady: "sesuai update admin",
          preorderStatus: "",
          productType: b.productType,
          lines: [{ variationId: choice.variationId, variationName: choice.variationName, unitPrice, qty: b.qty, ...paired }]
        };
        next = [...next, entry];
        continue;
      }
      const target = next[idx];
      const lineIdx = target.lines.findIndex((l) => l.variationId === choice.variationId);
      const lines: CartLine[] =
        lineIdx === -1
          ? [
              ...target.lines,
              { variationId: choice.variationId, variationName: choice.variationName, unitPrice, qty: b.qty, ...paired }
            ]
          : target.lines.map((l, i) => {
              if (i !== lineIdx) return l;
              if (l.unitPrice !== undefined) return { ...l, qty: l.qty + b.qty };
              // D14-31: harga yang baru diisi tak berpasangan dengan tambahan
              // lama baris itu — tambahannya dibuang (checkout membaca ulang).
              const { priceExtra: _unpaired, ...rest } = l;
              return { ...rest, qty: l.qty + b.qty, unitPrice, ...paired };
            });
      next = next.map((c, i) => (i === idx ? { ...c, lines } : c));
    }
  }
  return next;
}

/**
 * Pembeli SUDAH diberi tahu bahwa semua bundel dipesan harga satuan? —
 * harga terkini untuk SETIAP bundel termuat dan tak satu pun berlaku (kartu
 * memajang "Harga Anda saat ini lebih hemat dibeli satuan."). Bila ya,
 * cadangan BUNDLE_NONE_APPLIES boleh langsung memesan; bila tidak (harga
 * berubah sejak dilihat), keranjang diubah dulu dan pembeli menekan lagi.
 */
export function bundlesKnownNotApplying(
  bundles: readonly Pick<CartBundle, "key">[],
  liveByKey: ReadonlyMap<string, BundleLiveState> | null
): boolean {
  if (!bundles.length || !liveByKey) return false;
  return bundles.every((b) => {
    const live = liveByKey.get(b.key);
    return live?.status === "ok" && !live.entry.quote.applies;
  });
}

// ─── Pixel iklan ─────────────────────────────────────────────────────────────

/**
 * Keranjang pixel per PRODUK untuk checkout ber-bundel: baris biasa (harga
 * yang diberikan pemanggil — sesudah voucher bila ada) + baris komponen bundel
 * (harga akhir). Satu entri per produk, urut kemunculan pertama — RPC membuat
 * satu pesanan per produk, jadi Purchase per pesanan membaca entri produknya.
 */
export function bundleCheckoutPixelCarts(
  carts: readonly PixelCart[],
  bundles: readonly Pick<CartBundle, "key" | "qty" | "choices">[],
  unitPriceOf: (bundleKey: string, componentId: string) => number | null | undefined
): PixelCart[] {
  const order: string[] = [];
  const byProduct = new Map<string, PixelCart>();
  const add = (productId: string, productName: string, lines: PixelCart["lines"]) => {
    const cur = byProduct.get(productId);
    if (cur) {
      cur.lines.push(...lines);
      return;
    }
    order.push(productId);
    byProduct.set(productId, { productId, productName, lines: [...lines] });
  };
  for (const c of carts) add(c.productId, c.productName, c.lines.map((l) => ({ ...l })));
  for (const b of bundles) {
    for (const choice of b.choices) {
      const unit = unitPriceOf(b.key, choice.componentId);
      add(choice.productId, choice.productName, [
        isPrice(unit) ? { qty: b.qty, unitPrice: unit } : { qty: b.qty }
      ]);
    }
  }
  return order.map((id) => byProduct.get(id) as PixelCart);
}

/** Σ qty × harga satu keranjang pixel; null bila ada baris tanpa harga (idiom `cartSubtotal`). */
export function pixelCartSubtotal(cart: PixelCart): number | null {
  if (!cart.lines.length) return null;
  let total = 0;
  for (const l of cart.lines) {
    if (!isPrice(l.unitPrice)) return null;
    total += l.unitPrice * l.qty;
  }
  return total;
}

/**
 * Keranjang biasa → keranjang pixel dengan harga SESUDAH voucher untuk baris
 * yang dijawab pratinjau (kunci `<cart>:<baris>`, cermin `resolveCartsPricing`).
 * Tanpa pratinjau = harga potret keranjang.
 */
export function cartsAsPixelCarts(
  carts: readonly CartState[],
  previewLines: readonly CartVoucherPreviewLine[] | null
): PixelCart[] {
  const after = previewFinalByKey(previewLines);
  return carts.map((c, ci) => ({
    productId: c.productId,
    productName: c.productName,
    lines: c.lines.map((l, li) => {
      const unit = after.get(`${ci + 1}:${li + 1}`) ?? l.unitPrice;
      return isPrice(unit) ? { qty: l.qty, unitPrice: unit } : { qty: l.qty };
    })
  }));
}
